import { FARE, KERB_LANE, farePesewas } from '@accra/shared';
import type { EventBus } from '../../core/events';
import type { Rng } from '../../core/rng';
import type { Passenger, RunState } from '../../game/RunState';
import { isSettledIn } from '../lanes';
import { STOP_BOX_HALF, isInsideBox, lastSeqOfTrip, stopAt, stopsPerTrip, type StopPoint } from './stopSequence';
import { STEP_INTERVAL, advanceWalkers, boardersWalking, cancelBoarders, startWalker } from './walking';

const STOPPED_SPEED = 0.6;
const PERFECT_OFFSET = 1.5;
/** Passengers pick a destination 1..MAX_RIDE stops ahead. */
const MAX_RIDE = 4;
/** Every stop, at least this many riders get down (if anyone is aboard). */
const MIN_ALIGHTING = 1;
const MAX_EXTRA_ALIGHTING = 2;

export function beginStop(state: RunState, seq: number, rng: Rng): void {
  const point = stopAt(seq);
  const [min, max] = point.stop.crowd;
  // Nobody boards at the terminus you're arriving at: they wait on the other side for the trip back.
  const waiting = point.isTripEnd ? 0 : Math.round(rng.int(min, max) * state.theme.crowdMultiplier * state.effects.crowd);
  state.stop = { seq, waiting, stopped: false, rated: false, stepTimer: 0.3, approachAnnounced: false, alightQueue: [] };
}

function isStoppedInBox(state: RunState, point: StopPoint): boolean {
  const { bus } = state;
  return bus.speed < STOPPED_SPEED && bus.y === 0 && isSettledIn(bus.x, KERB_LANE) && isInsideBox(bus.s, point, bus.size.length);
}

function isPerfect(state: RunState, point: StopPoint): boolean {
  return Math.abs(state.bus.s - point.position) < PERFECT_OFFSET;
}

/** Everyone whose stop this is gets down, and if nobody is, one or two riders shout "Bus stop!" anyway. At the terminus, everyone gets down. */
function chooseAlighting(state: RunState, point: StopPoint, rng: Rng): Passenger[] {
  if (point.isTripEnd) return [...state.passengers];
  const due = state.passengers.filter((p) => p.destination === point.seq);
  if (due.length >= MIN_ALIGHTING || state.passengers.length === 0) return due;
  const others = state.passengers.filter((p) => p.destination !== point.seq);
  const extra = Math.min(others.length, rng.int(MIN_ALIGHTING, MAX_EXTRA_ALIGHTING));
  for (let i = 0; i < extra; i += 1) {
    const rider = others.splice(rng.int(0, others.length - 1), 1)[0];
    if (rider) {
      rider.destination = point.seq;
      due.push(rider);
    }
  }
  return due;
}

function arrive(state: RunState, point: StopPoint, rng: Rng, events: EventBus): void {
  const perfect = isPerfect(state, point);
  state.stop.stopped = true;
  state.stop.rated = true;
  if (perfect) state.stats.perfectStops += 1;
  events.emit('stop:rated', { stopName: point.stop.name, rating: perfect ? 'perfect' : 'good' });
  state.stop.alightQueue = chooseAlighting(state, point, rng);
}

function stepOff(state: RunState, point: StopPoint, rider: Passenger, events: EventBus): void {
  const fare = farePesewas(state.bus.s - rider.boardedAtS, state.theme.fareMultiplier * state.effects.fare);
  const tip = isPerfect(state, point) ? Math.round(FARE.perfectStopTipPesewas * state.effects.tip) : 0;
  state.passengers = state.passengers.filter((p) => p !== rider);
  state.stats.faresPesewas += fare + tip;
  state.stats.passengersDropped += 1;
  startWalker(state, 'alight', 0);
  events.emit('passenger:dropped', { stopName: point.stop.name, farePesewas: fare, tipPesewas: tip });
}

/** The next person in the crowd starts walking to the door, if there's a seat for them. */
function sendNextBoarder(state: RunState): void {
  const seatsLeft = state.bus.capacity - state.passengers.length - boardersWalking(state);
  if (state.stop.waiting > 0 && seatsLeft > 0) {
    state.stop.waiting -= 1;
    startWalker(state, 'board', state.stop.waiting);
  }
}

function board(state: RunState, point: StopPoint, rng: Rng, events: EventBus): void {
  const ride = rng.int(1, Math.min(MAX_RIDE, stopsPerTrip() - 1));
  // Nobody rides past the terminus of this trip.
  state.passengers.push({ destination: Math.min(point.seq + ride, lastSeqOfTrip(point.seq)), boardedAtS: state.bus.s });
  state.stats.passengersCarried += 1;
  events.emit('passenger:boarded', { stopName: point.stop.name, onBoard: state.passengers.length });
}

/** Passengers whose stop is behind us get down without paying. */
function dropMissed(state: RunState, point: StopPoint, events: EventBus): void {
  const missed = state.passengers.filter((p) => p.destination === point.seq).length;
  if (missed === 0) return;
  state.passengers = state.passengers.filter((p) => p.destination !== point.seq);
  state.stats.stopsMissed += 1;
  events.emit('passenger:missed', { stopName: point.stop.name, count: missed });
}

function runStop(state: RunState, point: StopPoint, dt: number, rng: Rng, events: EventBus): void {
  if (!state.stop.stopped) arrive(state, point, rng, events);
  state.stop.stepTimer -= dt;
  if (state.stop.stepTimer > 0) return;
  state.stop.stepTimer = STEP_INTERVAL;
  // Riders get down first, then the crowd boards, one person at a time.
  const rider = state.stop.alightQueue.shift();
  if (rider) stepOff(state, point, rider, events);
  else sendNextBoarder(state);
}

/** Runs the current stop: rating, people getting off and on, and moving on once the bus leaves. */
export function updateStop(state: RunState, dt: number, rng: Rng, events: EventBus): StopPoint {
  const point = stopAt(state.stop.seq);
  for (const walker of advanceWalkers(state, dt)) {
    if (walker.kind === 'board') board(state, point, rng, events);
  }
  if (isStoppedInBox(state, point)) runStop(state, point, dt, rng, events);
  else if (state.stop.stopped && state.bus.speed > STOPPED_SPEED) cancelBoarders(state);

  if (state.bus.s > point.position + STOP_BOX_HALF) {
    const hadBusiness = state.stop.waiting > 0 || state.passengers.some((p) => p.destination === point.seq);
    if (!state.stop.stopped && hadBusiness) events.emit('stop:rated', { stopName: point.stop.name, rating: 'overshot' });
    cancelBoarders(state);
    dropMissed(state, point, events);
    beginStop(state, point.seq + 1, rng);
  }
  return point;
}
